import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { PhoneFormatPipe } from 'src/app/shared/pipes/phone-format.pipe';
import { environment } from '../../../../environments/environment';

interface CustomersResponse {
  success: boolean;
  data: {
    customers: any[];
    total: number;
    page: number;
    limit: number;
  };
}

interface FieldsResponse {
  success: boolean;
  data: {
    fields: any[];
  };
}

/**
 * Organization Customers Tab Component
 *
 * Displays a read-only paginated and searchable table of customers for an organization.
 * Supports expandable rows that lazy-load field data on first expand.
 * Fields are cached in a Map to avoid re-fetching on subsequent expands.
 */
@Component({
  selector: 'app-organization-customers-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, PhoneFormatPipe],
  templateUrl: './organization-customers-tab.component.html',
  styleUrls: ['./organization-customers-tab.component.scss']
})
export class OrganizationCustomersTabComponent implements OnInit {
  @Input() organizationId!: string;

  customers: any[] = [];
  loading = true;
  errorMessage = '';

  // Expand state
  expandedCustomers: Set<string> = new Set();
  customerFields: Map<string, any[]> = new Map();
  loadingFields: Set<string> = new Set();

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalCustomers = 0;
  totalPages = 0;

  // Search
  searchQuery = '';

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    if (!this.organizationId) {
      this.errorMessage = 'Organization ID is required';
      this.loading = false;
      return;
    }

    this.loadCustomers();
  }

  async loadCustomers(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const params = new URLSearchParams({
        page: this.currentPage.toString(),
        limit: this.pageSize.toString()
      });

      if (this.searchQuery.trim()) {
        params.append('search', this.searchQuery.trim());
      }

      const response = await firstValueFrom(
        this.http.get<CustomersResponse>(
          `${environment.apiUrl}/admin/organizations/${this.organizationId}/customers?${params.toString()}`
        )
      );

      if (response.success && response.data) {
        this.customers = response.data.customers;
        this.totalCustomers = response.data.total;
        this.totalPages = Math.ceil(this.totalCustomers / this.pageSize);
      }
    } catch (error: any) {
      console.error('Failed to load customers:', error);
      this.errorMessage = 'Failed to load customers. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadCustomers();
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadCustomers();
    }
  }

  toggleCustomer(customerId: string): void {
    if (this.expandedCustomers.has(customerId)) {
      this.expandedCustomers.delete(customerId);
    } else {
      this.expandedCustomers.add(customerId);
      // Lazy-load fields on first expand only
      if (!this.customerFields.has(customerId)) {
        this.loadFields(customerId);
      }
    }
  }

  isExpanded(customerId: string): boolean {
    return this.expandedCustomers.has(customerId);
  }

  isLoadingFields(customerId: string): boolean {
    return this.loadingFields.has(customerId);
  }

  getFields(customerId: string): any[] {
    return this.customerFields.get(customerId) || [];
  }

  async loadFields(customerId: string): Promise<void> {
    this.loadingFields.add(customerId);

    try {
      const response = await firstValueFrom(
        this.http.get<FieldsResponse>(
          `${environment.apiUrl}/admin/organizations/${this.organizationId}/customers/${customerId}/fields`
        )
      );

      if (response.success && response.data) {
        this.customerFields.set(customerId, response.data.fields);
      }
    } catch (error: any) {
      console.error('Failed to load fields for customer:', error);
    } finally {
      this.loadingFields.delete(customerId);
    }
  }

  formatLocation(city: string | null, state: string | null): string {
    const parts = [city, state].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '-';
  }

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  get showingStart(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalCustomers);
  }
}
